import { Component, computed, inject, linkedSignal, signal, WritableSignal } from '@angular/core';
import { MigrationStatistics, RCumLaudeAccount, RInstelling, RVestiging } from '@cumlaude/service-contract';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RestService } from '@cumlaude/shared-services';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { Option } from '@cumlaude/shared-utils';
import { InstellingMigratieSelectieListComponent } from './instelling-migratie-selectie-list/instelling-migratie-selectie-list.component';
import { InstellingMigratieMatchSectiesComponent } from './instelling-migratie-match-secties/instelling-migratie-match-secties.component';
import { MatchCard } from './types/match.card';
import { MatchGroup } from './types/match.group';

type Match<T> = {
	doel?: T;
	bron: T[];
};

type VestigingMatch = Match<RVestiging>;

type AccountMatch = Match<RCumLaudeAccount>;

const STAPPEN = [
	{ nummer: 1, label: 'Doelschool' },
	{ nummer: 2, label: 'Bronscholen' },
	{ nummer: 3, label: 'Vestigingen' },
	{ nummer: 4, label: 'Accounts' },
	{ nummer: 5, label: 'Uitvoeren' },
] as const;

@Component({
	selector: 'app-instelling-migratie',
	templateUrl: './instelling-migratie.component.html',
	styleUrls: ['./instelling-migratie.component.scss'],
	imports: [ButtonComponent, InstellingMigratieSelectieListComponent, InstellingMigratieMatchSectiesComponent],
})
export class InstellingMigratieComponent {
	private readonly restService = inject(RestService);

	readonly currentStap = signal(1);
	readonly stappen = STAPPEN;

	readonly isExecuting = signal(false);
	readonly executionError = signal<string | undefined>(undefined);
	readonly executionSuccess = signal(false);
	readonly executionStatistics = signal<MigrationStatistics | undefined>(undefined);

	readonly instellingen = toSignal(this.restService.getInstellingen());

	readonly bronInstellingen = signal<RInstelling[]>([]);

	readonly doelInstelling = signal<RInstelling | undefined>(undefined);

	readonly doelSelectedIds = computed<string[]>(() => {
		const doelInstelling = this.doelInstelling();
		return doelInstelling?.id ? [doelInstelling.id] : [];
	});

	readonly doelDisabledIds = computed<string[]>(() => this.collectInstellingIds(this.bronInstellingen()));

	readonly bronSelectedIds = computed<string[]>(() => this.collectInstellingIds(this.bronInstellingen()));

	readonly bronDisabledIds = computed<string[]>(() => {
		const doelInstelling = this.doelInstelling();
		return doelInstelling?.id ? [doelInstelling.id] : [];
	});

	readonly bronVestigingen = toSignal(
		toObservable(this.bronInstellingen).pipe(
			switchMap((instellingen) => (instellingen.length === 0 ? of([]) : this.restService.getVestigingen({ instelling: instellingen })))
		),
		{ initialValue: [] }
	);

	readonly doelVestigingen = toSignal(
		toObservable(this.doelInstelling).pipe(
			switchMap((instelling) => (instelling === undefined ? of([]) : this.restService.getVestigingen({ instelling })))
		),
		{
			initialValue: [],
		}
	);

	readonly matchVestigingen = computed<VestigingMatch[]>(() => {
		let bronVestigingen = [...this.bronVestigingen()];
		const doelVestigingen = this.sortVestigingen(this.doelVestigingen());

		const matchedVestigingen: VestigingMatch[] = [];
		for (const doelVestiging of doelVestigingen) {
			const matched = bronVestigingen.filter(
				(bronVestiging) => this.normalizeValue(bronVestiging.naam) === this.normalizeValue(doelVestiging.naam)
			);
			matchedVestigingen.push({ doel: doelVestiging, bron: matched });
			bronVestigingen = bronVestigingen.filter(
				(bronVestiging) => !matched.some((matchedVestiging) => matchedVestiging.id === bronVestiging.id)
			);
		}

		matchedVestigingen.splice(0, 0, { doel: undefined, bron: bronVestigingen });

		for (const match of matchedVestigingen) match.bron = this.sortVestigingen(match.bron);

		return matchedVestigingen;
	});

	readonly matchedVestigingen = linkedSignal(() => this.matchVestigingen());

	readonly autoMatchedVestigingen = computed(
		() => new Set(this.matchVestigingen().flatMap((match, index) => (index > 0 && match.bron.length > 0 ? [index] : [])))
	);

	readonly vestigingMatchGroup = computed(() =>
		this.createMatchGroup(
			this.matchedVestigingen(),
			this.sortVestigingen,
			(index) => this.autoMatchedVestigingen().has(index),
			(vestiging) => `${vestiging.naam}${vestiging.actief ? '' : ' (Inactief)'}`,
			(vestiging) => !vestiging.actief
		)
	);

	readonly vestigingDisplayCards = computed<MatchCard<RVestiging>[]>(() => this.vestigingMatchGroup().cards);

	readonly vestigingCardsAutoMatch = computed(() => this.vestigingDisplayCards().filter((match) => match.autoMatch));

	readonly vestigingCardsManualMatch = computed(() => this.vestigingDisplayCards().filter((match) => !match.autoMatch));

	readonly bronAccounts = toSignal(
		toObservable(this.bronInstellingen).pipe(
			switchMap((instellingen) =>
				instellingen.length === 0
					? of([])
					: forkJoin(instellingen.map((instelling) => this.restService.getAccountsByInstelling(instelling))).pipe(
							map((accountsPerInstelling) => accountsPerInstelling.flat())
						)
			)
		),
		{ initialValue: [] }
	);

	readonly doelAccounts = toSignal(
		toObservable(this.doelInstelling).pipe(
			switchMap((instelling) => (instelling === undefined ? of([]) : this.restService.getAccountsByInstelling(instelling)))
		),
		{ initialValue: [] }
	);

	readonly matchAccounts = computed<AccountMatch[]>(() => {
		let bronAccounts = this.sortAccounts(this.bronAccounts());
		const doelAccounts = this.sortAccounts(this.doelAccounts());

		const matchedAccounts: AccountMatch[] = doelAccounts.map((doelAccount) => ({ doel: doelAccount, bron: [] }));

		bronAccounts = this.matchAccountsByValue(matchedAccounts, doelAccounts, bronAccounts, (account) => account.email);
		bronAccounts = this.matchAccountsByValue(matchedAccounts, doelAccounts, bronAccounts, (account) => account.afkorting);
		bronAccounts = this.matchAccountsByValue(matchedAccounts, doelAccounts, bronAccounts, (account) => account.naam);

		matchedAccounts.splice(0, 0, { doel: undefined, bron: bronAccounts });

		for (const match of matchedAccounts) match.bron = this.sortAccounts(match.bron);

		return matchedAccounts;
	});

	readonly matchedAccounts = linkedSignal(() => this.matchAccounts());

	readonly autoMatchedAccounts = computed(
		() => new Set(this.matchAccounts().flatMap((match, index) => (index > 0 && match.bron.length > 0 ? [index] : [])))
	);

	readonly accountMatchGroup = computed(() =>
		this.createMatchGroup(
			this.matchedAccounts(),
			this.sortAccounts,
			(index) => this.autoMatchedAccounts().has(index),
			(account) => this.getBronAccountLabel(account)
		)
	);

	readonly accountDisplayCards = computed<MatchCard<RCumLaudeAccount>[]>(() => this.accountMatchGroup().cards);

	readonly accountCardsAutoMatched = computed(() => this.accountDisplayCards().filter((match) => match.autoMatch));

	readonly accountCardsManualMatch = computed(() => this.accountDisplayCards().filter((match) => !match.autoMatch));

	readonly canGoNext = computed(() => {
		switch (this.currentStap()) {
			case 1:
				return this.doelInstelling() !== undefined;
			case 2:
				return this.bronInstellingen().length > 0;
			default:
				return true;
		}
	});

	volgendeStap(): void {
		const stap = this.currentStap();
		if (stap < STAPPEN.length) this.currentStap.set(stap + 1);
	}

	vorigeStap(): void {
		const stap = this.currentStap();
		if (stap > 1) this.currentStap.set(stap - 1);
	}

	navigeerNaarStap(stapNummer: number): void {
		if (stapNummer < this.currentStap()) this.currentStap.set(stapNummer);
	}

	selectDoelInstelling(instelling: RInstelling): void {
		const current = this.doelInstelling();
		this.doelInstelling.set(current?.id === instelling.id ? undefined : instelling);
	}

	toggleBronInstelling(instelling: RInstelling): void {
		const current = this.bronInstellingen();
		if (current.some((i) => i.id === instelling.id)) {
			this.bronInstellingen.set(current.filter((i) => i.id !== instelling.id));
		} else {
			this.bronInstellingen.set([...current, instelling]);
		}
	}

	selectVestigingBron(matchIndex: number, vestigingen: RVestiging[]) {
		this.updateMatchSelection(this.matchedVestigingen, matchIndex, vestigingen, this.sortVestigingen);
	}

	selectAccountBron(matchIndex: number, accounts: RCumLaudeAccount[]) {
		this.updateMatchSelection(this.matchedAccounts, matchIndex, accounts, this.sortAccounts);
	}

	getBronAccountLabel(account: RCumLaudeAccount): string {
		const afkorting = account.afkorting ? ` (${account.afkorting})` : '';
		return `${account.naam}${afkorting}`;
	}

	private normalizeValue(value: string | undefined): string | undefined {
		const normalizedValue = (value ?? '').trim().toLowerCase();
		return normalizedValue === '' ? undefined : normalizedValue;
	}

	private readonly compareVestigingen = (a: RVestiging, b: RVestiging) => Number(b.actief) - Number(a.actief) || a.naam.localeCompare(b.naam);

	private readonly compareAccounts = (a: RCumLaudeAccount, b: RCumLaudeAccount) =>
		a.naam.localeCompare(b.naam) || (a.afkorting ?? '').localeCompare(b.afkorting ?? '') || (a.email ?? '').localeCompare(b.email ?? '');

	private readonly sortVestigingen = (vestigingen: RVestiging[]) => this.sortItems(vestigingen, this.compareVestigingen);

	private readonly sortAccounts = (accounts: RCumLaudeAccount[]) => this.sortItems(accounts, this.compareAccounts);

	private collectInstellingIds(instellingen: RInstelling[]): string[] {
		return instellingen.flatMap((instelling) => (instelling.id ? [instelling.id] : []));
	}

	private sortItems<T>(items: T[], compare: (a: T, b: T) => number): T[] {
		return [...items].sort(compare);
	}

	private matchAccountsByValue(
		matchedAccounts: AccountMatch[],
		doelAccounts: RCumLaudeAccount[],
		bronAccounts: RCumLaudeAccount[],
		valueSelector: (account: RCumLaudeAccount) => string | undefined
	): RCumLaudeAccount[] {
		let remainingBronAccounts = bronAccounts;

		for (const [index, doelAccount] of doelAccounts.entries()) {
			const doelValue = this.normalizeValue(valueSelector(doelAccount));
			if (doelValue === undefined) continue;

			const matched = remainingBronAccounts.filter((bronAccount) => {
				const bronValue = this.normalizeValue(valueSelector(bronAccount));
				return bronValue !== undefined && bronValue === doelValue;
			});
			matchedAccounts[index].bron.push(...matched);

			remainingBronAccounts = remainingBronAccounts.filter(
				(bronAccount) => !matched.some((matchedAccount) => matchedAccount.id === bronAccount.id)
			);
		}

		return remainingBronAccounts;
	}

	private createMatchGroup<T>(
		matches: Match<T>[],
		sortItems: (items: T[]) => T[],
		autoMatched: (index: number) => boolean,
		getOptionLabel: (item: T) => string,
		getOptionInactive?: (item: T) => boolean | undefined
	): MatchGroup<T> {
		const unmatched = sortItems(matches.at(0)?.bron ?? []);

		return {
			unmatched,
			cards: matches
				.slice(1)
				.filter((match): match is Match<T> & { doel: T } => match.doel !== undefined)
				.map((match, index) => {
					const bron = sortItems(match.bron);

					return {
						matchIndex: index + 1,
						doel: match.doel,
						doelLabel: getOptionLabel(match.doel),
						doelInactief: getOptionInactive?.(match.doel),
						bron,
						options: this.createOptions(bron, unmatched, getOptionLabel, getOptionInactive),
						autoMatch: autoMatched(index + 1),
					};
				}),
		};
	}

	private createOptions<T>(
		selectedItems: T[],
		unmatchedItems: T[],
		getOptionLabel: (item: T) => string,
		getOptionInactive?: (item: T) => boolean | undefined
	): Option<T>[] {
		return [...selectedItems, ...unmatchedItems].map(
			(item) => new Option(item, getOptionLabel(item), getOptionInactive?.(item) ? 'inactive' : undefined)
		);
	}

	private updateMatchSelection<T>(
		matchesSignal: WritableSignal<Match<T>[]>,
		matchIndex: number,
		selection: T[],
		sortItems: (items: T[]) => T[]
	): void {
		const matches = matchesSignal().map((match) => ({ ...match, bron: [...match.bron] }));
		const unmatched = matches.at(0);
		const currentMatch = matches.at(matchIndex);

		if (!unmatched || !currentMatch) return;

		const selectedItems = sortItems([...new Set(selection)]);
		const selectedSet = new Set(selectedItems);
		const availableItems = [...unmatched.bron, ...currentMatch.bron];

		currentMatch.bron = selectedItems;
		unmatched.bron = sortItems(availableItems.filter((item) => !selectedSet.has(item)));

		matchesSignal.set(matches);
	}

	executeMigration(): void {
		if (this.isExecuting() || this.executionSuccess()) return;

		this.isExecuting.set(true);
		this.executionError.set(undefined);

		const vestigingMatches = this.buildVestigingMatches();
		const accountMatches = this.buildAccountMatches();
		const bronInstellingIds = this.bronInstellingen()
			.map((inst) => inst.id)
			.filter((id): id is string => id !== undefined);

		const request = {
			doelInstellingId: this.doelInstelling()!.id!,
			bronInstellingIds,
			vestigingMatches,
			accountMatches,
		};

		this.restService.executeMigration(request).subscribe({
			next: (statistics) => {
				this.isExecuting.set(false);
				this.executionSuccess.set(true);
				this.executionStatistics.set(statistics);
			},
			error: (error) => {
				this.isExecuting.set(false);
				this.executionError.set('Migratie is mislukt');
				console.error('Migration error:', error);
			},
		});
	}

	private buildVestigingMatches(): Record<string, string> {
		const matches: Record<string, string> = {};

		for (const match of this.matchedVestigingen()) {
			if (match.doel?.id && match.bron.length > 0) {
				for (const bron of match.bron) {
					if (bron.id) {
						matches[bron.id] = match.doel.id;
					}
				}
			}
		}

		return matches;
	}

	private buildAccountMatches(): Record<string, string> {
		const matches: Record<string, string> = {};

		for (const match of this.matchedAccounts()) {
			if (match.doel?.id && match.bron.length > 0) {
				for (const bron of match.bron) {
					if (bron.id) {
						matches[bron.id] = match.doel.id;
					}
				}
			}
		}

		return matches;
	}
}
